import React, { useState, useEffect, useRef, useCallback } from 'react';
import { downloadVCard } from '../../../utils/vcard';
import './styles.css';

interface WebSpeechVoiceInputProps {
  onTranscription: (text: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (errorMessage: string) => void;
  maxDuration?: number;
  showForm?: boolean;
}

export const WebSpeechVoiceInput: React.FC<WebSpeechVoiceInputProps> = ({
  onTranscription,
  onStart = () => {},
  onEnd = () => {},
  onError = () => {},
  maxDuration = 14,
  showForm
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(maxDuration);
  const [isSpeechSupported, setIsSpeechSupported] = useState<boolean | null>(null);
  const [forceShowForm, setForceShowForm] = useState(false);
  
  // For fallback form
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [combinedInput, setCombinedInput] = useState('');
  const [showTooltip, setShowTooltip] = useState(true); // Show tooltip by default
  const combinedInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  
  // Function to parse name and phone from combined input
  const parseNameAndPhone = (input: string) => {
    // Look for a sequence of digits (possibly with spaces, dashes, or parentheses)
    const phoneRegex = /(?:\+?\d[\d\s\-()]{7,})/g;
    const phoneMatches = input.match(phoneRegex);
    
    if (phoneMatches && phoneMatches.length > 0) {
      // Extract the phone number
      const phoneNumber = phoneMatches[0].trim();
      
      // Remove the phone number from the input to get the name
      const nameText = input.replace(phoneRegex, '').trim();
      
      // Capitalize the first letter of each word in the name
      const capitalizedName = nameText
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      return { name: capitalizedName, phone: phoneNumber };
    }
    
    // If no phone number is found, assume it's all name
    // Capitalize the first letter of each word
    const capitalizedName = input
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    return { name: capitalizedName, phone: '' };
  };
  
  // Handle changes to the combined input
  const handleCombinedInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCombinedInput(value);
    
    // Parse name and phone
    const { name: parsedName, phone: parsedPhone } = parseNameAndPhone(value);
    setName(parsedName);
    setPhone(parsedPhone);
    
    // Hide tooltip once user starts typing
    if (value.trim().length > 0) {
      setShowTooltip(false);
    }
  };
  
  const recognitionRef = useRef<any>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  // Check browser support for Web Speech API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    // Log browser details for debugging
    console.log('Browser details:', {
      userAgent: navigator.userAgent,
      speechRecognitionSupported: !!SpeechRecognition,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    });
    
    // On mobile Chrome, sometimes the API exists but doesn't work properly
    // Force fallback UI on mobile devices for consistency
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      console.log('Mobile device detected, using fallback UI');
      setIsSpeechSupported(false);
    } else {
      setIsSpeechSupported(!!SpeechRecognition);
    }
    
    // Focus on name input if using fallback UI
    if ((!SpeechRecognition || isMobile) && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const handleRecognition = useCallback((text: string) => {
    // Parse name and phone from the recognized text
    const { name: parsedName, phone: parsedPhone } = parseNameAndPhone(text);
    
    // Set the combined input and individual fields
    setCombinedInput(text);
    setName(parsedName);
    setPhone(parsedPhone);
    
    // If we have both name and phone, proceed with creating the contact
    if (parsedName && parsedPhone) {
      // Create a contact object
      const contact = {
        name: parsedName,
        phone: parsedPhone
      };
      
      // Download vCard
      downloadVCard(contact);
      
      // Call the original onTranscription callback
      onTranscription(text);
      
      // Hide the form after successful recognition
      setForceShowForm(false);
    }
  }, [onTranscription]);

  const stopRecording = async () => {
    try {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          console.error('Error stopping recognition:', err);
        }
        recognitionRef.current = null;
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      setIsRecording(false);
      setTimeLeft(maxDuration);
      onEnd();
    } catch (err) {
      console.error('Error stopping recording:', err);
      setError('Failed to stop recording properly');
      onError('Failed to stop recording properly');
    }
  };

  const startRecording = async () => {
    try {
      // Check if speech recognition is supported
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setError('Speech recognition not supported in this browser');
        onError('Speech recognition not supported in this browser');
        return;
      }

      // Initialize speech recognition
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript.trim();
        console.log('Speech recognized:', transcript);
        handleRecognition(transcript);
      };
      
      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setError(`Recognition error: ${event.error}`);
        onError(`Recognition error: ${event.error}`);
        setIsRecording(false);
      };
      
      recognitionRef.current.onend = () => {
        console.log('Speech recognition ended');
        setIsRecording(false);
        onEnd();
      };
      
      // Start recognition
      recognitionRef.current.start();
      setIsRecording(true);
      setTimeLeft(maxDuration);
      onStart();
      
      // Start timer
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to start recording');
      onError('Failed to start recording');
      if (recognitionRef.current) {
        recognitionRef.current = null;
      }
    }
  };

  const handleClick = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };

  // Handle form submission for fallback UI
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If we have a combined input but no parsed phone number yet, try to parse again
    if (combinedInput && !phone) {
      const { name: parsedName, phone: parsedPhone } = parseNameAndPhone(combinedInput);
      setName(parsedName);
      setPhone(parsedPhone);
      
      // If we still don't have a phone number, show an error or prompt
      if (!parsedPhone) {
        alert("Please include a phone number in your input");
        return;
      }
    }
    
    if (name && phone) {
      // Create a contact object to match the expected format
      const contact = {
        name,
        phone
      };
      
      // Download vCard directly without overlay
      downloadVCard(contact);
      
      // Call onTranscription with the combined data
      onTranscription(`${name} ${phone}`);
      
      // Hide the form after successful submission
      setForceShowForm(false);
      
      // Reset form
      setName('');
      setPhone('');
      setCombinedInput('');
      
      // Focus back on combined input
      if (combinedInputRef.current) {
        combinedInputRef.current.focus();
      }
    } else {
      alert("Please enter both name and phone number");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          console.error('Error stopping recognition on unmount:', err);
        }
        recognitionRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Reset form visibility when showForm prop changes
  useEffect(() => {
    if (showForm !== undefined) {
      setForceShowForm(showForm);
    }
  }, [showForm]);

  // If speech recognition is not supported or forceShowForm is true, show the fallback form UI
  if (isSpeechSupported === false || forceShowForm) {
    return (
      <div className="voice-input-fallback">
        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label htmlFor="combined-input">Name and Phone Number</label>
            <input
              type="text"
              id="combined-input"
              value={combinedInput}
              onChange={handleCombinedInputChange}
              placeholder="Enter name and phone number"
              ref={combinedInputRef}
              autoFocus
              required
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setTimeout(() => setShowTooltip(false), 200)}
            />
            
            {showTooltip && !name && !phone && (
              <div className="tooltip">
                <svg className="mic-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z" fill="currentColor" />
                  <path d="M17.91 11C17.91 11.41 17.87 11.8 17.81 12.19C17.76 12.54 18.04 12.84 18.39 12.84H18.4C18.69 12.84 18.94 12.64 19 12.35C19.08 11.91 19.12 11.46 19.12 11C19.12 7.08 16.04 3.86 12.15 3.65C11.73 3.63 11.4 3.97 11.4 4.39V4.4C11.4 4.8 11.72 5.12 12.12 5.14C15.25 5.33 17.91 7.94 17.91 11Z" fill="currentColor" />
                  <path d="M12 15.5C9.42 15.5 7.25 13.33 7.25 10.75C7.25 10.34 6.91 10 6.5 10C6.09 10 5.75 10.34 5.75 10.75C5.75 14.16 8.59 17 12 17C15.41 17 18.25 14.16 18.25 10.75C18.25 10.34 17.91 10 17.5 10C17.09 10 16.75 10.34 16.75 10.75C16.75 13.33 14.58 15.5 12 15.5Z" fill="currentColor" />
                  <path d="M12 20.75C11.59 20.75 11.25 20.41 11.25 20V17C11.25 16.59 11.59 16.25 12 16.25C12.41 16.25 12.75 16.59 12.75 17V20C12.75 20.41 12.41 20.75 12 20.75Z" fill="currentColor" />
                  <path d="M15 22H9C8.59 22 8.25 21.66 8.25 21.25C8.25 20.84 8.59 20.5 9 20.5H15C15.41 20.5 15.75 20.84 15.75 21.25C15.75 21.66 15.41 22 15 22Z" fill="currentColor" />
                </svg>
                Tap the mic icon on your keyboard and speak the name and phone number
              </div>
            )}
            
            {(name || phone) && (
              <div className="parsed-results">
                {name && (
                  <div>
                    <span>Name:</span> {name}
                  </div>
                )}
                {phone && (
                  <div>
                    <span>Phone:</span> {phone}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {name && phone && (
            <button 
              type="submit"
              className="next-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="save-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              Add {name.split(' ')[0]} to contacts
            </button>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="voice-input">
      {error && <div className="error-message">{error}</div>}
      
      <div className="voice-button-container">
        <button
          className={`voice-button ${isRecording ? 'listening' : ''}`}
          onClick={handleClick}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          <svg
            className="microphone-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z"
              fill="currentColor"
            />
            <path
              d="M17.91 11C17.91 11.41 17.87 11.8 17.81 12.19C17.76 12.54 18.04 12.84 18.39 12.84H18.4C18.69 12.84 18.94 12.64 19 12.35C19.08 11.91 19.12 11.46 19.12 11C19.12 7.08 16.04 3.86 12.15 3.65C11.73 3.63 11.4 3.97 11.4 4.39V4.4C11.4 4.8 11.72 5.12 12.12 5.14C15.25 5.33 17.91 7.94 17.91 11Z"
              fill="currentColor"
            />
            <path
              d="M12 15.5C9.42 15.5 7.25 13.33 7.25 10.75C7.25 10.34 6.91 10 6.5 10C6.09 10 5.75 10.34 5.75 10.75C5.75 14.16 8.59 17 12 17C15.41 17 18.25 14.16 18.25 10.75C18.25 10.34 17.91 10 17.5 10C17.09 10 16.75 10.34 16.75 10.75C16.75 13.33 14.58 15.5 12 15.5Z"
              fill="currentColor"
            />
            <path
              d="M12 20.75C11.59 20.75 11.25 20.41 11.25 20V17C11.25 16.59 11.59 16.25 12 16.25C12.41 16.25 12.75 16.59 12.75 17V20C12.75 20.41 12.41 20.75 12 20.75Z"
              fill="currentColor"
            />
            <path
              d="M15 22H9C8.59 22 8.25 21.66 8.25 21.25C8.25 20.84 8.59 20.5 9 20.5H15C15.41 20.5 15.75 20.84 15.75 21.25C15.75 21.66 15.41 22 15 22Z"
              fill="currentColor"
            />
          </svg>
          {isRecording ? 'Listening...' : 'Tap to speak'}
        </button>
        
        {isRecording && (
          <div className="timer">
            {timeLeft}s
          </div>
        )}
      </div>
      
      <div className="fallback-link-container">
        <button 
          className="fallback-link"
          onClick={() => setForceShowForm(true)}
        >
          Having trouble? Click here to use text input instead
        </button>
      </div>
    </div>
  );
};

// Add type definitions for the Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
